import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { searchApi } from '../api';
import DashboardLayout from '../components/dashboard/DashboardLayout';
import { Search, Loader2, MessageSquare, LifeBuoy, FileText } from 'lucide-react';

function timeAgo(iso) {
  if (!iso) return '';
  const m = Math.floor((Date.now() - new Date(iso).getTime()) / 60000);
  if (m < 60) return `${m} min temu`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h} h temu`;
  return new Date(iso).toLocaleDateString('pl-PL');
}

/**
 * Full-text search po posts, comments, tickets. Backend używa Mongo text index.
 * Wymaga min. 2 znaki query. Wyniki: 3 sekcje (posty / komentarze / tickety).
 */
export default function SearchPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const [q, setQ] = useState(searchParams.get('q') || '');
  const [results, setResults] = useState({ posts: [], comments: [], tickets: [] });
  const [loading, setLoading] = useState(false);

  const runSearch = async (query) => {
    if (!query || query.length < 2) {
      setResults({ posts: [], comments: [], tickets: [] });
      return;
    }
    setLoading(true);
    try {
      const r = await searchApi.query(query);
      setResults({ posts: r.data?.posts || [], comments: r.data?.comments || [], tickets: r.data?.tickets || [] });
    } catch (e) { console.error(e); } finally { setLoading(false); }
  };

  useEffect(() => {
    if (q) runSearch(q);
  }, []);

  const onSubmit = (e) => {
    e.preventDefault();
    setSearchParams({ q });
    runSearch(q);
  };

  const totalResults = results.posts.length + results.comments.length + results.tickets.length;

  return (
    <DashboardLayout>
      <h1 className="text-2xl font-bold text-slate-900 mb-4 flex items-center gap-2">
        <Search className="w-6 h-6 text-indigo-500" /> Wyszukiwanie
      </h1>

      <form onSubmit={onSubmit} className="bg-white border border-slate-200 rounded-xl p-3 mb-4 flex gap-2">
        <input
          value={q}
          onChange={e => setQ(e.target.value)}
          placeholder="Wpisz frazę (min. 2 znaki)..."
          className="flex-1 px-3 py-2 border border-slate-200 rounded text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
          autoFocus
        />
        <button type="submit" disabled={loading || q.length < 2} className="px-4 py-2 bg-indigo-600 text-white text-sm rounded hover:bg-indigo-700 disabled:opacity-50 flex items-center gap-1">
          {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Search className="w-4 h-4" />} Szukaj
        </button>
      </form>

      {!loading && q && totalResults === 0 && (
        <div className="bg-white border border-dashed border-slate-300 rounded-lg p-8 text-center text-sm text-slate-500">
          <Search className="w-10 h-10 mx-auto mb-2 opacity-30" />
          Brak wyników dla "{q}".
        </div>
      )}

      {results.posts.length > 0 && (
        <section className="mb-6">
          <h2 className="text-sm font-semibold text-slate-500 uppercase tracking-wider mb-2 flex items-center gap-1">
            <FileText className="w-4 h-4" /> Posty ({results.posts.length})
          </h2>
          <div className="space-y-2">
            {results.posts.map(p => (
              <Link key={p.id} to={`/dashboard/community/${p.community_id}`} className="block bg-white rounded-lg border border-slate-200 p-3 hover:border-indigo-300 transition">
                <p className="text-sm text-slate-800 line-clamp-3">{p.content}</p>
                <div className="flex items-center gap-2 mt-2 text-[11px] text-slate-500">
                  <span className="px-1.5 py-0.5 bg-slate-100 rounded">{p.type}</span>
                  <span>{timeAgo(p.created_at)}</span>
                </div>
              </Link>
            ))}
          </div>
        </section>
      )}

      {results.comments.length > 0 && (
        <section className="mb-6">
          <h2 className="text-sm font-semibold text-slate-500 uppercase tracking-wider mb-2 flex items-center gap-1">
            <MessageSquare className="w-4 h-4" /> Komentarze ({results.comments.length})
          </h2>
          <div className="space-y-2">
            {results.comments.map(c => (
              <div key={c.id} className="bg-white rounded-lg border border-slate-200 p-3">
                <p className="text-sm text-slate-800 line-clamp-3">{c.content}</p>
                <div className="text-[11px] text-slate-500 mt-1">{timeAgo(c.created_at)}</div>
              </div>
            ))}
          </div>
        </section>
      )}

      {results.tickets.length > 0 && (
        <section className="mb-6">
          <h2 className="text-sm font-semibold text-slate-500 uppercase tracking-wider mb-2 flex items-center gap-1">
            <LifeBuoy className="w-4 h-4" /> Tickety ({results.tickets.length})
          </h2>
          <div className="space-y-2">
            {results.tickets.map(t => (
              <Link key={t.id} to={`/dashboard/my-tickets?ticket_id=${t.id}`} className="block bg-white rounded-lg border border-slate-200 p-3 hover:border-rose-300 transition">
                <h3 className="font-medium text-slate-900">{t.title}</h3>
                {t.description && <p className="text-sm text-slate-600 line-clamp-2 mt-1">{t.description}</p>}
                <div className="flex items-center gap-2 mt-2 text-[11px] text-slate-500">
                  <span className={`px-1.5 py-0.5 rounded ${t.status === 'resolved' ? 'bg-emerald-100 text-emerald-700' : t.status === 'in_progress' ? 'bg-blue-100 text-blue-700' : 'bg-amber-100 text-amber-700'}`}>
                    {t.status}
                  </span>
                  <span>{timeAgo(t.created_at)}</span>
                </div>
              </Link>
            ))}
          </div>
        </section>
      )}
    </DashboardLayout>
  );
}
